import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { X, Cookie } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { CookiePreferencesDialog } from './CookiePreferencesDialog';

export interface CookieConsent {
  necessary: boolean;
  functional: boolean;
  administrative: boolean;
  timestamp: number;
}

const DEFAULT_CONSENT: CookieConsent = {
  necessary: true,
  functional: false,
  administrative: false,
  timestamp: 0,
};

export function CookieBanner() {
  const location = useLocation();
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);

  // Check if we're on the Coming Soon page (root without preview param)
  const isComingSoonPage = location.pathname === '/' && !new URLSearchParams(location.search).get('preview');

  // Re-prompt for consent after this many days (GDPR best practice: refresh periodically)
  const CONSENT_VALIDITY_DAYS = 180;

  useEffect(() => {
    const consent = getConsentPreferences();
    const maxAgeMs = CONSENT_VALIDITY_DAYS * 24 * 60 * 60 * 1000;
    const expired = consent.timestamp > 0 && Date.now() - consent.timestamp > maxAgeMs;
    // Show banner only if no consent recorded, or saved consent is older than the validity window
    if ((consent.timestamp === 0 || expired) && !isComingSoonPage) {
      setShowBanner(true);
    }
  }, [isComingSoonPage]);

  const handleAcceptAll = () => {
    const consent: CookieConsent = {
      necessary: true,
      functional: true,
      administrative: true,
      timestamp: Date.now(),
    };
    saveConsentPreferences(consent);
    setShowBanner(false);
  };

  const handleRejectNonEssential = () => {
    const consent: CookieConsent = {
      necessary: true,
      functional: false,
      administrative: false,
      timestamp: Date.now(),
    };
    saveConsentPreferences(consent);
    setShowBanner(false);
  };

  // Don't show on Coming Soon page
  if (!showBanner || isComingSoonPage) {
    return null;
  }

  return (
    <>
      <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-background/80 backdrop-blur-sm border-t">
        <Card className="max-w-4xl mx-auto p-6 shadow-lg">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 mt-1">
              <Cookie className="h-6 w-6 text-primary" />
            </div>
            <div className="flex-1 space-y-4">
              <div>
                <h3 className="text-lg font-semibold mb-2">Cookie Preferences</h3>
                <p className="text-sm text-muted-foreground">
                  We use cookies to ensure our website functions properly and to store your preferences. 
                  Some cookies are essential for the site to work, whilst others help us improve your experience.{' '}
                  <Link to="/cookie-policy" className="text-primary hover:underline">
                    Learn more about our cookie policy
                  </Link>
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <Button onClick={handleAcceptAll} variant="default">
                  Accept All
                </Button>
                <Button onClick={handleRejectNonEssential} variant="outline">
                  Reject Non-Essential
                </Button>
                <Button onClick={() => setShowPreferences(true)} variant="ghost">
                  Manage Preferences
                </Button>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleRejectNonEssential}
              className="flex-shrink-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </Card>
      </div>

      <CookiePreferencesDialog
        open={showPreferences}
        onOpenChange={(open) => {
          setShowPreferences(open);
          if (!open) {
            // If they closed the dialog, check if they saved preferences
            const consent = getConsentPreferences();
            if (consent.timestamp > 0) {
              setShowBanner(false);
            }
          }
        }}
      />
    </>
  );
}

// Cookie consent management utilities
export function getConsentPreferences(): CookieConsent {
  try {
    const stored = localStorage.getItem('cookie-consent');
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.error('Error reading cookie consent:', e);
  }
  return DEFAULT_CONSENT;
}

export function saveConsentPreferences(consent: CookieConsent): void {
  try {
    localStorage.setItem('cookie-consent', JSON.stringify(consent));
    
    // Clear non-consented cookies
    if (!consent.functional) {
      localStorage.removeItem('dismissed_announcements');
      // Clear sidebar state cookie
      document.cookie = 'sidebar:state=; path=/; max-age=0';
    }
    if (!consent.administrative) {
      localStorage.removeItem('impersonation-state');
    }
  } catch (e) {
    console.error('Error saving cookie consent:', e);
  }
}

export function hasConsentFor(category: keyof Omit<CookieConsent, 'timestamp'>): boolean {
  const consent = getConsentPreferences();
  return consent[category] === true;
}
